(() => {
  const dateKey = (date) => date.toISOString().slice(0, 10);
  const addDay = (date) => new Date(date.getTime() + 86400000);
  function berlinDeadline(date, hour) {
    const wallTarget = date.getTime() + hour * 3600000;
    let instant = wallTarget;
    // Resolve the real Berlin instant, including the change between summer and winter time.
    for (let n = 0; n < 2; n++) {
      const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date(instant)).map((part) => [part.type, part.value]));
      instant += wallTarget - Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    }
    return new Date(instant);
  }
  function germanHolidays(year) {
    // Nationwide German public holidays. Calendar arithmetic uses UTC dates to avoid DST shifts.
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = (h + l - 7 * m + 114) % 31 + 1;
    const easter = Date.UTC(year, month - 1, day);
    return new Set([
      ...['01-01', '05-01', '10-03', '12-25', '12-26'].map((date) => `${year}-${date}`),
      ...[-2, 1, 39, 50].map((offset) => dateKey(new Date(easter + offset * 86400000))),
    ]);
  }
  function deliveryEstimate(now, minimum, maximum, cutoff = 14) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
    }).formatToParts(now).map((part) => [part.type, part.value]));
    const today = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
    const holidays = new Map();
    const workingDay = (date) => {
      const year = date.getUTCFullYear();
      if (!holidays.has(year)) holidays.set(year, germanHolidays(year));
      return date.getUTCDay() !== 0 && !holidays.get(year).has(dateKey(date));
    };
    let deadline = Number(parts.hour) < cutoff ? today : addDay(today);
    while (!workingDay(deadline)) deadline = addDay(deadline);
    const afterWorkingDays = (days) => {
      let date = deadline;
      for (let n = 0; n < days;) {
        date = addDay(date);
        if (workingDay(date)) n++;
      }
      return date;
    };
    return { today, deadline, deadlineAt: berlinDeadline(deadline, cutoff), earliest: afterWorkingDays(minimum), latest: afterWorkingDays(maximum) };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { deliveryEstimate };
  if (typeof window === 'undefined') return;
  if (!customElements.get('wh-product-delivery')) {
    customElements.define('wh-product-delivery', class extends HTMLElement {
      connectedCallback() {
        this.submit = this.closest('product-info')?.querySelector('product-form button[name="add"]');
        this.syncAvailability();
        this.update();
        this.timer = setInterval(() => this.update(), 1000);
        const submitLabel = this.submit?.querySelector('span');
        if (submitLabel) {
          // Shopify disables the button while loading; its label changes only when the result is ready.
          this.observer = new MutationObserver(() => this.syncAvailability());
          this.observer.observe(submitLabel, { childList: true, subtree: true, characterData: true });
        }
      }
      syncAvailability() {
        this.hidden = !this.submit || this.submit.disabled;
      }
      update() {
        const cutoff = Number(this.dataset.cutoff);
        const now = new Date();
        const estimate = deliveryEstimate(now, Number(this.dataset.minDays), Number(this.dataset.maxDays), cutoff);
        const seconds = Math.max(0, Math.floor((estimate.deadlineAt - now) / 1000));
        const hoursPart = String(Math.floor(seconds / 3600)).padStart(2, '0');
        const minutesPart = String(Math.floor(seconds / 60) % 60).padStart(2, '0');
        const secondsPart = String(seconds % 60).padStart(2, '0');
        const timer = this.querySelector('.wh-product-delivery__timer');
        if (timer) timer.textContent = `${hoursPart}:${minutesPart}:${secondsPart}`;
        const date = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric', timeZone: 'UTC' });
        this.querySelector('.wh-product-delivery__dates').textContent = date.format(estimate.latest);
      }
      disconnectedCallback() {
        clearInterval(this.timer);
        this.observer?.disconnect();
      }
    });
  }
  if (customElements.get('wh-product-purchase')) return;

  customElements.define('wh-product-purchase', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      this.submit = this.root?.querySelector('product-form button[name="add"]');
      this.price = this.root?.querySelector(`[id="price-${this.dataset.section}"]`);
      this.bar = this.querySelector('.wh-product-sticky');
      this.button = this.querySelector('button');
      if (!this.submit || !this.price || !this.bar) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.passedButton = false;

      this.observer = new IntersectionObserver(([entry]) => {
        this.passedButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.syncVisibility();
      });
      this.observer.observe(this.submit);
      this.mutations = new MutationObserver(() => this.sync());
      this.mutations.observe(this.price, { childList: true, subtree: true, characterData: true });
      this.mutations.observe(this.submit, { attributes: true, childList: true, subtree: true, characterData: true });
      this.mobile.addEventListener('change', () => this.syncVisibility(), { signal });
      // Also handle anchor jumps that skip past the button between observer callbacks.
      window.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.passedButton = this.submit.getBoundingClientRect().bottom <= 0;
          this.syncVisibility();
        });
      }, { signal, passive: true });

      this.button.addEventListener('click', () => {
        const picker = this.root.querySelector('variant-selects');
        if (this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true') {
          (picker || this.submit).scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
          picker?.querySelector('input:checked, select')?.focus({ preventScroll: true });
          return;
        }
        this.submit.click();
      }, { signal });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.section) return;
          const id = `WhProductFacts-${this.dataset.section}`;
          const source = data.html?.getElementById(id);
          const destination = this.root.querySelector(`[id="${id}"]`);
          if (source && destination) destination.innerHTML = source.innerHTML;
          this.sync();
        });
      }
      this.sync();
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    sync() {
      const amount = this.price.querySelector('.price--on-sale .price-item--sale') || this.price.querySelector('.price-item--regular');
      this.querySelector('.wh-product-sticky__price').textContent = amount?.textContent.trim() || '';
      const names = Array.from(this.root.querySelectorAll('variant-selects fieldset:not([hidden]) input:checked, variant-selects .product-form__input:not([hidden]) select'))
        .map((input) => input.value);
      this.querySelector('.wh-product-sticky__variant').textContent = names.join(' · ');
      this.button.textContent = this.submit.querySelector('span')?.textContent.trim() || this.submit.textContent.trim();
      this.button.disabled = this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true';
      this.syncVisibility();
    }

    syncVisibility() {
      this.bar.hidden = !(this.dataset.stickyEnabled !== 'false' && this.mobile.matches && this.passedButton);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.mutations?.disconnect();
      this.unsubscribe?.();
      cancelAnimationFrame(this.scrollFrame);
    }
  });
})();
